import { FeAllType, FeByType, FeConfigType, FeSubrecordType, RecordId } from "config/config";
import { templateCanvasPathFragment } from "utils/RouterManager/constants";

const ACTIVITY_TYPES = new Set<string>(["execution", "message", "process"]);

/** Activity record types always use ?id= in URLs (names are not unique). */
export const prefersIdInUrl = (recordType: string): boolean =>
    ACTIVITY_TYPES.has(recordType.toLowerCase());

export const getRelativeUrl = (
    recordType: FeAllType | FeSubrecordType,
    recordRef: RecordId,
    by: FeByType = "id"
) => `/${recordType.toLowerCase()}?${by}=${encodeURIComponent(recordRef)}`;

// Kept for use in backward-compat redirect routes only.
export const getRelativeRecordPath = (recordType: FeConfigType, recordId: RecordId, path: string) =>
    `/${recordType.toLowerCase()}/${recordId}/${path}`;

/** Base URL for the single workspace (no record identifier in the query string). */
export const getWorkspaceUrl = () => "/workspace";

export const getWorkspaceLink = (_workspaceId: RecordId, subPage: string) =>
    `${getWorkspaceUrl()}?${subPage}`;

export type CanvasType = "template" | "object";

export const getCanvasLink = (
    recordType: FeConfigType,
    recordRef: RecordId,
    canvasType: CanvasType = templateCanvasPathFragment,
    by: FeByType = "id",
    /**
     * When the canvas is opened from a specific record, pass that record's id
     * here to deep-link a focus target. The canvas highlights the matching node
     * and centers the view on it (see ObjectTemplateCanvas/Canvas.tsx `focus`).
     */
    focusRef?: RecordId
) =>
    `${getRelativeUrl(recordType, recordRef, by)}&canvas=${canvasType}${
        focusRef ? `&focus=${focusRef}` : ""
    }`;

/**
 * Link to a dashboard's rendered view. The rendered dashboard is the DEFAULT for
 * a dashboard record (no route flag) — the config editor lives behind `?edit`
 * (see {@link getDashboardEditLink}). Kept as a named helper so callers express
 * intent ("open the rendered dashboard") rather than a bare record link.
 */
export const getDashboardViewLink = (dashboardRef: RecordId, by: FeByType = "name") =>
    getRelativeUrl("dashboard", dashboardRef, by);

/** Link to a dashboard's config editor (the `?edit` route flag). */
export const getDashboardEditLink = (dashboardRef: RecordId, by: FeByType = "name") =>
    `${getRelativeUrl("dashboard", dashboardRef, by)}&edit`;

export const getTypedLowerCase = <T extends string>(string: T) =>
    (string || "").toLowerCase() as Lowercase<T>;

/**
 * True when a mouse click on a link/anchor opens it in a NEW browser tab rather
 * than navigating the current one — i.e. the user held a new-tab modifier
 * (Cmd/Ctrl/Shift) or used the middle mouse button. Used to decide whether an
 * in-app link click should keep transient UI (e.g. the quick-search popover)
 * open: a plain click navigates the current tab away and dismisses it, while a
 * new-tab click leaves the current view — and any open popover — untouched.
 */
export const opensInNewTab = (e?: {
    metaKey?: boolean;
    ctrlKey?: boolean;
    shiftKey?: boolean;
    button?: number;
}): boolean => Boolean(e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1));

export const getFullUrl = (relativeUrl) => {
    const typedWindow: any = window;
    return `${typedWindow.REACT_APP_REST_API || ""}${relativeUrl}`;
};

/** Fallback keeps storage keys stable when the build omits REACT_APP_VERSION_MAJOR. */
export const releaseVersion = process.env.REACT_APP_VERSION_MAJOR ?? "dev";

// to avoid local errors when switching between versions
export const getKeyWithReleaseVersion = (key: string) => (key ? `${key}-${releaseVersion}` : "");
/**
 * Get the item from localStorage with the current release version
 * @param key - the key to get the item from localStorage
 * @returns the item from localStorage
 */
export const getLocalStorageItem = (key: string) => {
    const keyWithReleaseVersion = getKeyWithReleaseVersion(key);
    return localStorage.getItem(keyWithReleaseVersion);
};
/**
 * Set the item in localStorage with the current release version
 * @param key - the key to set the item in localStorage
 * @param value - the value to set in localStorage, being stringified before setting in localStorage
 * @returns void
 */
export const setLocalStorageItem = (key: string, value: any) => {
    const keyWithReleaseVersion = getKeyWithReleaseVersion(key);
    localStorage.setItem(keyWithReleaseVersion, JSON.stringify(value));
};
/**
 * Remove the item from localStorage with the current release version
 * @param key - the key to remove the item from localStorage
 * @returns void
 */
export const removeLocalStorageItem = (key: string) => {
    const keyWithReleaseVersion = getKeyWithReleaseVersion(key);
    localStorage.removeItem(keyWithReleaseVersion);
};

export const getSessionStorageItem = (key: string) => {
    const keyWithReleaseVersion = getKeyWithReleaseVersion(key);
    return sessionStorage.getItem(keyWithReleaseVersion);
};

export const setSessionStorageItem = (key: string, value: any) => {
    const keyWithReleaseVersion = getKeyWithReleaseVersion(key);
    sessionStorage.setItem(keyWithReleaseVersion, JSON.stringify(value));
};

/**
 * Parse the JSON safely
 * @param value - the value to parse
 * @param defaultValue - the default value to return if the value is not a valid JSON
 * @returns the parsed value or the default value as a fallback
 */
export const parseJSONSafely = (value: string | null, defaultValue?: any) => {
    try {
        return value ? JSON.parse(value) : defaultValue;
    } catch (error) {
        return defaultValue;
    }
};
